import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { LoginFormValues, loginSchema } from "@/lib/schemas";
import { signIn } from "@/lib/supabase/auth";
import { toast } from "sonner";
import { AuthError } from "@/lib/supabase/errors";
import { debug } from "@/lib/utils";
import TradesPurpleLayout from "../components/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { Helmet } from "react-helmet-async";

export default function TradesPurpleLoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Initialize form at component top level to avoid rule of hooks violation
  const form = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // If already logged in, redirect to their trades page
  if (user?.username) {
    navigate(`/${user.username}`);
    return null;
  }

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    try {
      await signIn(data.email, data.password);

      toast.success("Logged in successfully");

      // CRITICAL BUG FIX: Can't use useAuth() inside an event handler
      // Use the imported useAuth hook above
      // Instead, wait for auth state to update naturally

      // Simply navigate to the homepage - we'll let the auth state redirect naturally
      navigate('/');

    } catch (error) {
      if (error instanceof AuthError) {
        toast.error(error.message);
      } else {
        toast.error("Failed to log in");
      }
      debug.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>TradesPurple | Log In</title>
        <meta name="description" content="Log in to TradesPurple to manage your trades list and settings." />
      </Helmet>

      <div className="flex items-center justify-center">
        <Card className="w-full border-2 border-purple-900 rounded-xl shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center" style={{ color: "#5D4A9C" }}>Welcome Back</CardTitle>
            <CardDescription className="text-center text-purple-700">
              Log in to manage your trusted tradespeople
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ color: "#5D4A9C" }}>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          className="border-2 border-purple-300 focus:border-purple-500"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel style={{ color: "#5D4A9C" }}>Password</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className="border-2 border-purple-300 focus:border-purple-500"
                          {...field}
                          disabled={isLoading}
                        />
                      </FormControl>
                      <FormMessage className="text-red-600" />
                    </FormItem>
                  )}
                />
                <Button
                  type="submit"
                  className="w-full bg-purple-700 hover:bg-purple-800"
                  disabled={isLoading}
                >
                  {isLoading ? "Logging in..." : "Log in"}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex flex-col space-y-2">
            <div className="text-sm text-center text-purple-700">
              <Link to="/auth/forgot-password" style={{ color: "#5D4A9C" }} className="font-semibold hover:underline">
                Forgot password?
              </Link>
            </div>
            <div className="text-sm text-center text-purple-700">
              Don't have an account yet?{" "}
              <Link to="/auth/signup" style={{ color: "#5D4A9C" }} className="font-semibold hover:underline">
                Sign up
              </Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </TradesPurpleLayout>
  );
}
